class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.openTimeout = null;
  }

  connectedCallback() {
    if (document.referrer) {
      if (document.referrer.includes("/cart")) {
        this.open();
      }
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    
    // Clear any pending open timeout to prevent race conditions
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    // Clear any pending open timeout to prevent it from reopening the drawer
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Clear any existing timeout to prevent orphaned timers if renderContents is called multiple times
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    
    // Store the timeout ID so it can be cancelled if close() is called
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

/** Cart drawer recommended products horizontal slider: arrows + scroll sync */
function initCartDrawerRecommendationSliders(root) {
  root
    .querySelectorAll(".recomendations_wraper__viewport:not([data-rec-slider-bound])")
    .forEach((viewport) => {
      viewport.dataset.recSliderBound = "1";
      const track = viewport.querySelector(".recomendations_wraper__track");
      const prev = viewport.querySelector(".recomendations_wraper__arrow--prev");
      const next = viewport.querySelector(".recomendations_wraper__arrow--next");
      if (!track || !prev || !next) return;

      function syncArrows() {
        const max = track.scrollWidth - track.clientWidth;
        const left = track.scrollLeft;
        prev.toggleAttribute("disabled", left <= 1);
        next.toggleAttribute("disabled", left >= max - 1);
      }

      track.addEventListener("scroll", syncArrows, { passive: true });
      if (typeof ResizeObserver !== "undefined") {
        new ResizeObserver(syncArrows).observe(track);
      }
      syncArrows();

      function scrollStep() {
        const first = track.querySelector(".recomended_card");
        const gap = 12;
        return first ? first.offsetWidth + gap : track.clientWidth;
      }

      prev.addEventListener("click", () => {
        track.scrollBy({ left: -scrollStep(), behavior: "smooth" });
      });
      next.addEventListener("click", () => {
        track.scrollBy({ left: scrollStep(), behavior: "smooth" });
      });
    });
}

function runCartDrawerRecSliderInit() {
  initCartDrawerRecommendationSliders(document);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", runCartDrawerRecSliderInit);
} else {
  runCartDrawerRecSliderInit();
}

const cartDrawerHost = document.querySelector("cart-drawer");
if (cartDrawerHost) {
  new MutationObserver(runCartDrawerRecSliderInit).observe(cartDrawerHost, {
    childList: true,
    subtree: true,
  });
}
